/**
 * @file Post model for the SRAC club CMS.
 *
 * Defines the Firestore-backed `posts/{id}` shape for announcements and
 * news content authored from the admin Post editor. Includes optional
 * linkage fields that point at a `raceEvents` doc and/or an `events` doc
 * so a single post can advertise an upcoming race or club event and
 * surface a contextual card on the post detail page (and, eventually,
 * pull artwork into a distribution email).
 *
 * @copyright Snohomish Radio Aero Club. See LICENSE in the repo root.
 */

/**
 * Visibility tier of a {@link Post}. Drives query-side filtering on
 * {@link `/posts`} and access checks in firestore.rules.
 */
export type PostVisibility = 'public' | 'members' | 'officers' | 'unlisted';

/**
 * A single CMS post / announcement persisted under `posts/{id}`.
 */
export interface Post {
  id: string;
  title: string;
  content: string; // Markdown content
  authorId: string;
  authorName: string;
  visibility: PostVisibility;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date; // Optional, for scheduled/draft posts
  isDraft?: boolean;
  /**
   * Optional Firestore doc id under `raceEvents`. **Legacy** —
   * superseded by {@link linkedRaceIds} (a post about a race day can
   * cover multiple races on the same morning). Readers should prefer
   * `linkedRaceIds`, falling back to `[linkedRaceId]` when only the
   * legacy field is set so existing posts keep working. `null` and
   * `undefined` both mean "not linked".
   */
  linkedRaceId?: string | null;
  /**
   * Optional ordered list of Firestore doc ids under `raceEvents`.
   * When set, the post detail page renders one "Linked race" card
   * per id and the distribution email pulls the first race's banner
   * imagery. New writes should use this field exclusively.
   */
  linkedRaceIds?: string[];
  /**
   * Optional Firestore doc id under `events`. When set, the post detail
   * page renders a "Linked event" card and (eventually) the
   * distribution email pulls the event's banner art. `null` and
   * `undefined` both mean "not linked".
   */
  linkedEventId?: string | null;
}

/**
 * Resolve a post's effective list of linked race ids — prefers the
 * new `linkedRaceIds` field, falls back to a one-element list built
 * from the legacy `linkedRaceId` field, and returns `[]` when neither
 * is set. Use this everywhere instead of touching the raw fields so
 * old posts keep rendering correctly.
 */
export function resolveLinkedRaceIds(post: Pick<Post, 'linkedRaceIds' | 'linkedRaceId'>): string[] {
  if (Array.isArray(post.linkedRaceIds) && post.linkedRaceIds.length > 0) {
    return post.linkedRaceIds.filter((id): id is string => typeof id === 'string' && !!id);
  }
  if (typeof post.linkedRaceId === 'string' && post.linkedRaceId) {
    return [post.linkedRaceId];
  }
  return [];
}

/**
 * Get visibility label for display
 */
export function getVisibilityLabel(visibility: PostVisibility): string {
  const labels: Record<PostVisibility, string> = {
    public: 'Public',
    members: 'Members Only',
    officers: 'Officers Only',
    unlisted: 'Unlisted (Link Only)',
  };
  return labels[visibility];
}

/**
 * Get visibility badge variant
 */
export function getVisibilityVariant(visibility: PostVisibility): 'default' | 'secondary' | 'destructive' | 'outline' {
  const variants: Record<PostVisibility, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    public: 'default',
    members: 'secondary',
    officers: 'destructive',
    unlisted: 'outline',
  };
  return variants[visibility];
}
